const normalize = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

export const planningContractTypes = {
  cdi: { label: "CDI", className: "border-blue-200 bg-blue-50 text-blue-900" },
  cdd: { label: "CDD", className: "border-violet-200 bg-violet-50 text-violet-900" },
  vacation: { label: "Vacation", className: "border-pink-200 bg-pink-50 text-pink-900" },
  interim: { label: "Intérim", className: "border-emerald-200 bg-emerald-50 text-emerald-900" },
};

/**
 * Libellé du type réel.
 * Placement CDI / Placement CDD, Vacation, CDD, CDI et Intérim sont repris tels quels.
 * Sur un contrat PLD (Tempo), TypeContrat est un code : 1 = contrat initial, 4 = prolongation.
 * Ces codes ne sont pas un CDI/CDD : la mission reste de l'intérim.
 */
export function planningContractType(contract = {}) {
  const raw =
    contract.contractType ||
    contract.typeContrat ||
    contract.TypeContrat ||
    contract.NatureContrat ||
    contract.service;
  const key = normalize(raw);
  if ((key.includes("placement") && key.includes("cdi")) || key === "cdi") {
    return { ...planningContractTypes.cdi, label: key.includes("placement") ? "Placement CDI" : "CDI" };
  }
  if ((key.includes("placement") && key.includes("cdd")) || key === "cdd") {
    return { ...planningContractTypes.cdd, label: key.includes("placement") ? "Placement CDD" : "CDD" };
  }
  if (key.includes("vac")) return planningContractTypes.vacation;
  if (key.includes("interim") || key.includes("travail temporaire") || key === "ctt") {
    return planningContractTypes.interim;
  }
  if (/^\d+$/.test(key)) return planningContractTypes.interim;
  const pldContract =
    !contract.isOpenPosition &&
    (contract.NumeroContrat || contract.IdContrat || contract.HoraireAMDebut || contract.HorairePMFin || contract.IdInterimaire);
  if (pldContract) return planningContractTypes.interim;
  return { label: "Type non renseigné", className: "border-slate-200 bg-slate-50 text-slate-700" };
}

/**
 * Couleur du planning, raccordée au statut back :
 * - vert : contrat affecté, signé (signed, signedByUser, signedByClient)
 *   ou dématérialisé (isDemat, isDematUser, isDematClient),
 *   ou déjà pourvu d'un intérimaire (IdInterimaire / NomPrenom / candidate).
 *   Statuts : Dématérialisé, Signé, Placement, Facturé, En cours.
 * - orange : poste à pourvoir (commande) ou contrat sans candidat affecté.
 * - non-lieu (EstNonLieu = O) et annulé : masqués, jamais affichés en vert.
 */
export const planningTones = {
  open: {
    label: "Poste à pourvoir",
    className: "border-orange-300 border-l-orange-600 bg-orange-50 text-orange-950",
  },
  assigned: {
    label: "Contrat affecté",
    className: "border-emerald-300 border-l-emerald-600 bg-emerald-50 text-emerald-950",
  },
};

const ASSIGNED_STATUSES = new Set(["dematerialise", "signe", "affecte", "placement", "facture", "facturee", "en cours"]);

export function isClosedPlanningContract(contract = {}) {
  const nonLieu = String(contract.EstNonLieu ?? "").trim().toUpperCase();
  if (["O", "OUI", "TRUE", "1"].includes(nonLieu)) return true;
  const status = normalize(contract.status || contract.statut);
  if (["annule", "annulee", "non lieu", "nonlieu"].includes(status)) return true;
  return contract.cancelled === true || contract.canceled === true;
}

export function planningTone(contract = {}) {
  if (contract.isContractRequest) return {label:"Contrat demandé",className:"border-slate-300 border-l-slate-500 bg-slate-100 text-slate-800"};
  if (contract.isOpenPosition) return planningTones.open;
  if (contract.isConfirmedPosition) return { ...planningTones.assigned, label: 'Candidat confirmé' };
  const status = normalize(contract.status || contract.statut);
  const dematOrSigned = Boolean(
    contract.isDemat ||
      contract.isDematUser ||
      contract.isDematClient ||
      contract.signed ||
      contract.signedByUser ||
      contract.signedByClient,
  );
  const named = String(contract.NomPrenom || "").trim();
  const hasWorker = Boolean(
    (contract.IdInterimaire && !["0", ""].includes(String(contract.IdInterimaire).trim())) ||
      contract.candidate ||
      (named && !normalize(named).includes("a pourvoir")),
  );
  if (dematOrSigned || ASSIGNED_STATUSES.has(status) || hasWorker) return planningTones.assigned;
  return planningTones.open;
}
